import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, CheckCircle2, Loader2, ArrowLeft, AlertTriangle, Send, Shield, MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import trainingApi from '../services/trainingApi';

const AudienceBanner = ({ note }) => {
  if (!note) return null;
  return (
    <div className="mt-3 mb-1 rounded-2xl bg-amber-50 border-2 border-amber-400 px-4 py-3.5 text-center shadow-sm shadow-amber-200/40">
      <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700 mb-1">Dla kogo</p>
      <p className="inline-flex items-center justify-center gap-2 text-lg sm:text-xl font-extrabold text-amber-950 leading-snug">
        <MapPin className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 text-amber-700" />
        {note}
      </p>
    </div>
  );
};

const normalizeOptions = (options) => {
  if (!options || !options.length) return [];
  return options.map((opt) => {
    if (typeof opt === 'string') return { value: opt, label: opt };
    return { value: String(opt.value ?? ''), label: String(opt.label ?? opt.value ?? '') };
  });
};

const SurveyQuestionField = ({ question, value, onChange, disabled }) => {
  const type = (question.questionType || 'TEXT').toUpperCase();
  const options = normalizeOptions(question.options);

  if (type === 'TEXTAREA') {
    return (
      <textarea
        rows={4}
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50"
        placeholder="Twoja odpowiedź…"
      />
    );
  }

  if (type === 'SINGLE_CHOICE') {
    return (
      <div className="space-y-2">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
              value === opt.value
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'opacity-70 cursor-default' : ''}`}
          >
            <input
              type="radio"
              name={`q-${question.id}`}
              checked={value === opt.value}
              disabled={disabled}
              onChange={() => onChange(opt.value)}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (type === 'MULTI_CHOICE') {
    const selected = value ? value.split('\n').filter(Boolean) : [];
    const toggle = (v) => {
      const next = selected.includes(v)
        ? selected.filter((x) => x !== v)
        : [...selected, v];
      onChange(next.join('\n'));
    };
    return (
      <div className="space-y-2">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
              selected.includes(opt.value)
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'opacity-70 cursor-default' : ''}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(opt.value)}
              disabled={disabled}
              onChange={() => toggle(opt.value)}
              className="rounded text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (type === 'SCALE') {
    const scaleOpts = options.length > 0
      ? options
      : ['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: v }));
    const hasTextLabels = scaleOpts.some((o) => o.label && o.label !== o.value);

    return (
      <div className={`grid gap-2 ${hasTextLabels ? 'grid-cols-1 sm:grid-cols-5' : 'grid-cols-5'}`}>
        {scaleOpts.map((opt) => {
          const selected = value === opt.value;
          const showOnlyNumber = !opt.label || opt.label === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={`min-h-[72px] sm:min-h-[94px] px-2 py-3 rounded-xl border text-center transition-colors flex flex-col items-center justify-center gap-2 ${
                selected
                  ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              } disabled:opacity-70 ${hasTextLabels ? 'sm:flex-col flex-row sm:justify-center justify-start gap-3 sm:gap-2 px-4 sm:px-2' : ''}`}
            >
              <span className="text-lg font-bold text-gray-900">{opt.value}</span>
              {!showOnlyNumber && (
                <span className="text-xs leading-snug text-gray-600 sm:text-center text-left">
                  {opt.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <input
      type="text"
      value={value || ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50"
      placeholder="Twoja odpowiedź…"
    />
  );
};

const AnonymityBanner = () => (
  <div className="flex items-start gap-3 p-4 mb-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900">
    <Shield className="h-5 w-5 shrink-0 mt-0.5 text-emerald-700" />
    <div className="text-sm leading-relaxed">
      <p className="font-bold">Ankieta anonimowa</p>
      <p className="mt-1 text-emerald-800">
        Nie zapisujemy treści odpowiedzi pod Twoim kontem. Do raportów w CRM trafia wyłącznie treść
        odpowiedzi — bez powiązania z imieniem/loginem. Każde konto może wypełnić ankietę tylko raz.
      </p>
    </div>
  </div>
);

const SurveyForm = ({ surveyId, onBack }) => {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await trainingApi.get(`/surveys/${surveyId}`);
        if (cancelled) return;
        setSurvey(res.data);
        const initial = {};
        (res.data.answers || []).forEach((a) => {
          initial[a.questionId] = a.answerText || '';
        });
        setAnswers(initial);
      } catch (e) {
        toast.error('Nie udało się pobrać ankiety');
        onBack();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [surveyId, onBack]);

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!survey || survey.completed) return;

    for (const q of survey.questions || []) {
      if (!q.required) continue;
      const val = (answers[q.id] || '').trim();
      if (!val) {
        toast.error(`Uzupełnij wymagane pytanie: ${q.questionText}`);
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        answers: Object.entries(answers)
          .filter(([, v]) => (v || '').trim())
          .map(([questionId, answerText]) => ({
            questionId: Number(questionId),
            answerText: String(answerText).trim(),
          })),
      };
      await trainingApi.post(`/surveys/${surveyId}/submit`, payload);
      toast.success(survey.anonymous
        ? 'Dziękujemy! Odpowiedź zapisana anonimowo (jednorazowo).'
        : 'Ankieta zapisana. Dziękujemy!');

      const refreshed = await trainingApi.get(`/surveys/${surveyId}`);
      setSurvey(refreshed.data);
      const initial = {};
      (refreshed.data.answers || []).forEach((a) => {
        initial[a.questionId] = a.answerText || '';
      });
      setAnswers(initial);
    } catch (err) {
      const msg = err.response?.data?.error || 'Nie udało się zapisać ankiety';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Ładowanie ankiety…
      </div>
    );
  }

  if (!survey) return null;

  const anonymous = !!survey.anonymous;
  const completed = !!survey.completed;
  const lockForm = completed || submitting;

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-indigo-700 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Wróć do listy ankiet
      </button>

      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6 sm:p-8">
        <div className="flex items-start gap-3 mb-2">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">{survey.title}</h2>
              {anonymous && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 px-2 py-1 rounded-md">
                  <Shield className="h-3 w-3" /> Anonimowa
                </span>
              )}
            </div>
            {survey.description && (
              <p className="text-sm text-gray-500 mt-1">{survey.description}</p>
            )}
            <AudienceBanner note={survey.audienceNote} />
          </div>
          {completed && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg">
              <CheckCircle2 className="h-3.5 w-3.5" /> Wypełniona
            </span>
          )}
        </div>

        {anonymous && <AnonymityBanner />}

        {completed && (
          <div className="mt-4 mb-6 flex items-start gap-2 p-3 bg-green-50 border border-green-100 rounded-xl text-sm text-green-800">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <p>
              Ankieta została już wypełniona
              {survey.submittedAt ? ` (${new Date(survey.submittedAt).toLocaleString('pl-PL')})` : ''}.
              Nie można jej edytować ani wypełniać ponownie
              {anonymous ? ' — odpowiedzi pozostają anonimowe (bez podglądu treści).' : '.'}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-2 space-y-6">
          {(survey.questions || []).map((q, idx) => (
            <div key={q.id} className="border-t border-gray-100 pt-5 first:border-0 first:pt-0">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                <span className="text-indigo-600 mr-1.5">{idx + 1}.</span>
                {q.questionText}
                {q.required && <span className="text-amber-600 ml-1">*</span>}
              </label>
              {completed && anonymous ? (
                <p className="text-sm text-gray-400 italic">Treść odpowiedzi nie jest dostępna (ankieta anonimowa).</p>
              ) : (
                <SurveyQuestionField
                  question={q}
                  value={answers[q.id] || ''}
                  onChange={(v) => setAnswer(q.id, v)}
                  disabled={lockForm}
                />
              )}
            </div>
          ))}

          {!completed && (
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 py-2.5 px-6 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Zapisywanie…</>
                ) : (
                  <><Send className="h-4 w-4" /> Wyślij ankietę</>
                )}
              </button>
              <p className="text-xs text-gray-500">
                {anonymous
                  ? 'Wysyłka anonimowa — jedno konto może wypełnić tylko raz.'
                  : 'Jedno konto może wypełnić ankietę tylko raz.'}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const SurveySection = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const loadSurveys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await trainingApi.get('/surveys');
      setSurveys(res.data || []);
    } catch (e) {
      toast.error('Nie udało się pobrać listy ankiet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  if (selectedId) {
    return (
      <SurveyForm
        surveyId={selectedId}
        onBack={() => {
          setSelectedId(null);
          loadSurveys();
        }}
      />
    );
  }

  const completedCount = surveys.filter((s) => s.completed).length;
  const pendingCount = surveys.filter((s) => !s.completed).length;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ankiety</h1>
        <p className="text-sm text-gray-500 mt-1">
          Wypełnij dostępne ankiety. Ankiety oznaczone jako anonimowe nie zapisują, kto je wypełnił.
        </p>
      </div>

      {!loading && surveys.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 ring-1 ring-black/5 flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold text-gray-900 leading-none">{pendingCount}</p>
              <p className="text-xs text-gray-500 mt-1">do wypełnienia</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 ring-1 ring-black/5 flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-green-50 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-bold text-green-600 leading-none">{completedCount}</p>
              <p className="text-xs text-gray-500 mt-1">wypełnionych</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Ładowanie ankiet…
        </div>
      ) : surveys.length === 0 ? (
        <div className="bg-white rounded-xl ring-1 ring-black/5 p-10 text-center text-gray-500">
          <ClipboardList className="h-10 w-10 mx-auto text-gray-300 mb-3" />
          Brak dostępnych ankiet. Zajrzyj tu później.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {surveys.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelectedId(s.id)}
              className="text-left bg-white rounded-2xl ring-1 ring-black/5 p-5 hover:shadow-lg hover:ring-indigo-200 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <ClipboardList className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {s.anonymous && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 px-2 py-1 rounded-md">
                      <Shield className="h-3 w-3" /> Anonimowa
                    </span>
                  )}
                  {s.completed ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-green-700 bg-green-100 px-2 py-1 rounded-md">
                      <CheckCircle2 className="h-3 w-3" /> Wypełniona
                    </span>
                  ) : s.required ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded-md">
                      Obowiązkowa
                    </span>
                  ) : null}
                </div>
              </div>
              <h3 className="mt-3 font-semibold text-gray-900 leading-snug">{s.title}</h3>
              {s.audienceNote && (
                <div className="mt-3 rounded-xl bg-amber-50 border-2 border-amber-400 px-3 py-3 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-0.5">Dla kogo</p>
                  <p className="inline-flex items-center justify-center gap-1.5 text-base font-extrabold text-amber-950 leading-snug">
                    <MapPin className="h-4 w-4 shrink-0 text-amber-700" />
                    {s.audienceNote}
                  </p>
                </div>
              )}
              {s.description && (
                <p className="mt-2 text-xs text-gray-500 line-clamp-2">{s.description}</p>
              )}
              {s.anonymous && (
                <p className="mt-2 text-xs text-emerald-700 font-medium">
                  Anonimowa · jedno konto = jedna wysyłka.
                </p>
              )}
              <p className="mt-4 text-sm font-semibold text-indigo-600">
                {s.completed
                  ? (s.anonymous ? 'Już wypełniona →' : 'Zobacz status →')
                  : 'Wypełnij ankietę →'}
              </p>
            </button>
          ))}
        </div>
      )}

      {!loading && surveys.some((s) => s.required && !s.completed) && (
        <div className="mt-6 flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <p>Masz obowiązkowe ankiety do wypełnienia.</p>
        </div>
      )}
    </>
  );
};

export default SurveySection;
